/**
 * TargetCard Component
 *
 * Individual target display card with progress visualization.
 *
 * Date: 2026-03-11
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, MoreVertical, Trash2 } from 'lucide-react';
import { PipelineProgress } from './PipelineProgress';
import type { PipelineTarget } from '@/services/pipelineTrackerService';
import { formatPriorityLevels, deleteTarget } from '@/services/pipelineTrackerService';
import { formatDistanceToNow } from 'date-fns';

interface TargetCardProps {
  target: PipelineTarget;
  onRefresh: () => void;
}

export function TargetCard({ target, onRefresh }: TargetCardProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { status } = target;

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteTarget(target.id);
      onRefresh();
    } catch (error) {
      console.error('Failed to delete target:', error);
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return '-';
    }
  };

  return (
    <>
      <Card className="mb-4">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold">{target.name}</h3>
                {!target.is_active && (
                  <Badge variant="secondary">Inactive</Badge>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                <span>{target.site_name}</span>
                {target.category_paths?.length > 0 && (
                  <>
                    <span>•</span>
                    <span>{target.category_paths.length} categories</span>
                  </>
                )}
                <span>•</span>
                <span>{formatPriorityLevels(target.priority_levels)}</span>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => setShowDeleteDialog(true)}
                  className="text-red-600"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent>
          {/* Overall Progress Bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium">Overall Progress</span>
              <span className="text-sm text-gray-500">{status.overallProgress}%</span>
            </div>
            <Progress value={status.overallProgress} className="h-2" />
          </div>

          {/* Pipeline Stages */}
          <PipelineProgress status={status} />

          {/* Blocked Warning */}
          {status.blockedReason && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Blocked</p>
                <p className="text-sm text-amber-700">{status.blockedReason}</p>
              </div>
            </div>
          )}

          {/* Discovery Ready Highlight */}
          {status.discoveryReady && !status.blockedReason && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm font-medium text-green-800">
                Discovery Ready - Products are searchable via conversational discovery
              </p>
            </div>
          )}

          {/* Footer Stats */}
          <div className="mt-4 pt-4 border-t flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-4">
              <span>Schedule: {status.schedule || 'Manual'}</span>
              <span>Auto-approve: {status.autoApprove ? 'Yes' : 'No'}</span>
              {status.generationLimit && (
                <span>Limit: {status.generationLimit}</span>
              )}
            </div>
            <div className="flex items-center gap-4">
              {status.firstApproval && (
                <span>First: {formatDate(status.firstApproval)}</span>
              )}
              {status.lastApproval && (
                <span>Last: {formatDate(status.lastApproval)}</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Target</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{target.name}"? This will remove the target from
              your tracking list. This action can be undone by an admin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
